import { notify } from '@billy/design-system'
import noop from 'lodash/noop'
import { createContext, ReactElement, ReactNode, useCallback, useContext, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useInvoiceComplianceMode } from '@features/invoices/hooks/useInvoiceComplianceMode'
import { useUpdateCompanyInformation } from '@features/invoicingSettings/hooks/useUpdateCompanyInformation'
import { useVatConfirmationWarningModal } from '@features/invoicingSettings/hooks/useVatConfirmationWarningModal'
import { useMarkAccountStepDone } from '@features/quickStart/hooks/useMarkAccountStepDone'
import { mapCompanyFormToAccountInfo } from '@features/quickStart/utils'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { NotificationKeys } from '../../../../../../../enums/notificationKeys'
import { useForm, UseFormReturn } from '../../../../../../../hooks'
import { useMissingComplianceDetails } from '../../../hooks/missingComplianceDetails/useMissingComplianceDetails'
import { usePredefinedCompanyValues } from '../../../hooks/missingComplianceDetails/usePredefinedCompanyValues'
import { MissingCompanyDetailsForm } from '../../../types/missingComplianceDetails/missingCompanyDetailsForm'
import { MissingCompanyDetailsFormSchema } from '../types/missingCompanyDetailsFormSchema'
import { getCompanyFormInformationUpdate } from '../utils/getCompanyFormInformationUpdate'
import { getCompanyFormValidationSchemaByComplianceMode } from '../utils/getCompanyFormValidationSchemaByComplianceMode'

interface ContextState {
  isLoading: boolean
  isSubmitting: boolean
  missingCompanyDetailsForm: UseFormReturn<MissingCompanyDetailsFormSchema>
  onConfirm: (() => void) | null
  submitForm: (onSuccess: () => void) => void
  validate: () => Promise<boolean>
}

const MissingCompanyDetailsFormContext = createContext<ContextState | undefined>(undefined)

interface MissingCompanyDetailsFormProviderProps {
  children?: ReactNode
  onConfirm: (() => void) | null
}

export const MissingCompanyDetailsFormContextProvider = ({
  children,
  onConfirm,
}: MissingCompanyDetailsFormProviderProps): ReactElement => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { isLoading, predefinedCompanyValues } = usePredefinedCompanyValues()
  const { invoiceComplianceMode } = useInvoiceComplianceMode()
  const { isMissingCompanyPaymentInfo } = useMissingComplianceDetails()
  const markAccountStepDone = useMarkAccountStepDone()

  const { update: updateCompanyInformation, isProcessing: isSubmitting } = useUpdateCompanyInformation({
    onError: () => {
      notify({
        id: NotificationKeys.CompanyInformationUpdated,
        message: t('external_invoices.missing_company_compliance_details.modal.form.company_update.notification.error'),
        variant: 'error',
      })
    },
    onSuccess: (formData) => {
      notify({
        id: NotificationKeys.CompanyInformationUpdated,
        message: t(
          'external_invoices.missing_company_compliance_details.modal.form.company_update.notification.success',
        ),
        variant: 'success',
      })
      markAccountStepDone(mapCompanyFormToAccountInfo(formData, organization?.countryId))
    },
  })

  const missingCompanyDetailsForm = useForm<MissingCompanyDetailsFormSchema>({
    validationSchema: getCompanyFormValidationSchemaByComplianceMode({
      complianceMode: invoiceComplianceMode,
      countryId: organization?.countryId,
      t,
    }),
  })
  const { saveWithWarning, modals: warningModals } = useVatConfirmationWarningModal()

  const { handleSubmit, reset: resetForm, trigger } = missingCompanyDetailsForm

  useEffect(() => {
    if (predefinedCompanyValues && !isLoading) {
      resetForm(predefinedCompanyValues)
    }
  }, [isLoading, predefinedCompanyValues, resetForm])

  // we're using a fake submit for validation due to how useForm's mode param works
  const handleValidation = useCallback(async () => {
    await handleSubmit(noop)()

    return trigger()
  }, [handleSubmit, trigger])

  const handleSubmitWithWarning = useCallback(
    (values: MissingCompanyDetailsForm, onSuccess: () => void) => {
      if (!organization) {
        return
      }
      saveWithWarning({
        values,
        valuesToCompare: {
          isVatExempted: organization.isVatExempted,
        },
        onWarningsConfirm: () =>
          updateCompanyInformation(
            getCompanyFormInformationUpdate(values, { isPaymentInfoEditable: isMissingCompanyPaymentInfo }),
            { onSuccess },
          ),
      })
    },
    [isMissingCompanyPaymentInfo, organization, saveWithWarning, updateCompanyInformation],
  )

  const submitForm = useCallback(
    (onSuccess: () => void) => {
      handleSubmit((formData) => handleSubmitWithWarning(formData, onSuccess))()
    },
    [handleSubmit, handleSubmitWithWarning],
  )

  const contextValue = useMemo(
    () => ({
      isLoading,
      isSubmitting,
      missingCompanyDetailsForm,
      onConfirm,
      submitForm,
      validate: handleValidation,
    }),
    [handleValidation, isLoading, isSubmitting, missingCompanyDetailsForm, onConfirm, submitForm],
  )

  return (
    <MissingCompanyDetailsFormContext.Provider value={contextValue}>
      {children}
      {warningModals}
    </MissingCompanyDetailsFormContext.Provider>
  )
}

export const useMissingCompanyDetailsForm = () => {
  const context = useContext(MissingCompanyDetailsFormContext)

  if (!context) {
    throw new Error('MissingCompanyDetailsFormContextProvider is missing in the module!')
  }

  return context
}
